
import { motion } from 'framer-motion';
import { useState } from 'react';

const romanticPackages = [
  {
    name: 'A Little Something',
    subtitle: 'a soft, thoughtful surprise',
    icon: 'ri-heart-3-fill',
    gradient: 'from-pink to-coral',
    borderColor: 'border-pink',
    features: [
      'Ceiling balloon install',
      'Rose petal bed design',
      'Romantic message banner',
      'LED candles',
      'Fairy lights'
    ],
    price: '225',
    illustration: 'https://public.readdy.ai/ai/img_res/8fd73a04ccd29a72c8ada58a713620bc.jpg'
  },
  {
    name: 'Just Because',
    subtitle: 'perfect for anniversaries or "just because" moments',
    icon: 'ri-heart-fill',
    gradient: 'from-coral to-pink',
    borderColor: 'border-coral',
    features: [
      'Full ceiling balloons',
      'Rose petal bed + pathway',
      'Custom message or name display',
      'LED candles',
      'Fairy lights',
      'Hanging accents (photos or roses)'
    ],
    price: '300',
    illustration: 'https://readdy.ai/api/search-image?query=elegant%20romantic%20room%20decoration%20with%20balloons%20rose%20petals%20and%20candles%20in%20coral%20pink%20tones%20minimalist%20artistic%20style%20clean%20background&width=400&height=300&seq=pkg002&orientation=landscape'
  },
  {
    name: 'All Yours',
    subtitle: 'a fully curated romantic experience',
    icon: 'ri-hearts-fill',
    gradient: 'from-pink to-coral',
    borderColor: 'border-pink',
    features: [
      'Ceiling + floor balloons',
      'Full rose petal styling throughout room',
      'Custom name or phrase',
      'LED candles & enhanced lighting',
      'Hanging photos and roses'
    ],
    price: '375',
    illustration: 'https://readdy.ai/api/search-image?query=luxurious%20romantic%20room%20setup%20with%20balloons%20rose%20petals%20candles%20and%20hanging%20decorations%20in%20pink%20coral%20colors%20artistic%20minimalist%20design%20clean%20background&width=400&height=300&seq=pkg003&orientation=landscape'
  }
];

const birthdayPackages = [
  {
    name: 'Make A Wish',
    subtitle: 'a simple and stylish birthday surprise',
    icon: 'ri-cake-3-fill',
    gradient: 'from-purple to-pink',
    borderColor: 'border-purple',
    features: [
      'Happy birthday banner',
      'Ceiling balloons',
      'Rose petal bed design',
      'LED candles',
      'Fairy lights'
    ],
    price: '225',
    illustration: 'https://readdy.ai/api/search-image?query=minimalist%20birthday%20celebration%20with%20balloons%20candles%20and%20banner%20in%20purple%20pink%20colors%20simple%20clean%20design%20on%20white%20background&width=400&height=300&seq=pkg004&orientation=landscape'
  },
  {
    name: 'Birthday Glow',
    subtitle: 'a polished birthday setup with extra details',
    icon: 'ri-gift-fill',
    gradient: 'from-coral to-purple',
    borderColor: 'border-coral',
    features: [
      'Custom birthday message',
      'Ceiling + floor balloons',
      'Rose petal bed + pathway',
      'LED candles',
      'Fairy lights',
      'Hanging photos'
    ],
    price: '275',
    illustration: 'https://readdy.ai/api/search-image?query=elegant%20birthday%20room%20decoration%20with%20balloons%20rose%20petals%20and%20hanging%20photos%20in%20coral%20purple%20tones%20minimalist%20artistic%20style%20clean%20background&width=400&height=300&seq=pkg005&orientation=landscape'
  },
  {
    name: 'All About You',
    subtitle: 'a luxe birthday room transformation',
    icon: 'ri-gift-2-fill',
    gradient: 'from-purple to-pink',
    borderColor: 'border-purple',
    features: [
      'Ceiling + floor balloons',
      'Custom name or phrase',
      'Enhanced lighting and candles',
      'Hanging photos and roses',
      'Full room styling'
    ],
    price: '350',
    illustration: 'https://readdy.ai/api/search-image?query=luxurious%20birthday%20room%20transformation%20with%20balloons%20custom%20decorations%20and%20full%20styling%20in%20purple%20pink%20colors%20artistic%20minimalist%20design%20clean%20background&width=400&height=300&seq=pkg006&orientation=landscape'
  }
];

const addOns = [
  { name: 'Extra Balloons', price: '$25-$50' },
  { name: 'Custom Name or Phrase', price: '$40' },
  { name: 'Additional Rose Petals', price: '$25' },
  { name: 'Hanging Photos', price: '$30' },
  { name: 'Handmade Artificial Rose Bouquet', price: '$65' },
  { name: 'Fresh Rose Bouquet', price: '$65' }
];

export default function Packages() {
  const [activeTab, setActiveTab] = useState<'romantic' | 'birthday'>('romantic');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentPackages = activeTab === 'romantic' ? romanticPackages : birthdayPackages;

  return (
    <section id="packages" className="py-20 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="text-gray-400 text-sm mb-4">//official price list</div>
          <h2 className="font-serif font-bold text-4xl md:text-5xl text-charcoal mb-4">
            <strong>Surprise Packages</strong> Tailored to You
          </h2>
          <p className="text-gray-600 font-light text-lg mb-8">
            Mix, match, and customize every detail
          </p>

          {/* Tab Switcher */}
          <div className="inline-flex bg-cream rounded-full p-1">
            <button
              onClick={() => setActiveTab('romantic')}
              className={`px-6 py-2 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'romantic'
                  ? 'bg-gradient-to-r from-pink to-coral text-white shadow-lg'
                  : 'text-charcoal hover:text-coral'
              }`}
            >
              <i className="ri-heart-3-fill mr-2"></i>Romantic
            </button>
            <button
              onClick={() => setActiveTab('birthday')}
              className={`px-6 py-2 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'birthday'
                  ? 'bg-gradient-to-r from-purple to-pink text-white shadow-lg'
                  : 'text-charcoal hover:text-purple'
              }`}
            >
              <i className="ri-cake-3-fill mr-2"></i>Birthday
            </button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16" data-product-shop>
          {currentPackages.map((pkg, index) => (
            <article key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className={`relative bg-white rounded-3xl border-2 ${pkg.borderColor} p-8 shadow-xl hover:shadow-2xl transition-all h-full flex flex-col`}
              >
                <div className="w-full h-48 mb-6 rounded-2xl overflow-hidden bg-gradient-to-br from-cream to-white flex items-center justify-center">
                  <img 
                    src={pkg.illustration} 
                    alt={`${pkg.name} surprise decoration package`}
                    title={`${pkg.name} - surprise decorations`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className={`w-16 h-16 bg-gradient-to-br ${pkg.gradient} rounded-2xl flex items-center justify-center mb-4 mx-auto`}>
                  <i className={`${pkg.icon} text-white text-3xl`}></i>
                </div>

                <h3 className="font-serif font-bold text-2xl text-charcoal text-center mb-2">
                  {pkg.name}
                </h3>
                <p className="text-sm text-gray-500 text-center mb-6 italic">
                  {pkg.subtitle}
                </p>

                <ul className="space-y-3 mb-8 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-coral mt-0.5 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-charcoal">
                    ${pkg.price}
                  </div>
                </div>

                <button 
                  onClick={() => scrollToSection('contact')}
                  className={`w-full py-3 border-2 ${pkg.borderColor} text-charcoal font-semibold rounded-full hover:bg-gradient-to-r ${pkg.gradient} hover:text-white hover:border-transparent transition-all cursor-pointer whitespace-nowrap`}
                >
                  Book Now
                </button>
              </motion.div>
            </article>
          ))}
        </div>

        {/* Add-Ons Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-cream rounded-3xl p-8 md:p-12 mb-12"
        >
          <h3 className="font-serif font-bold text-3xl text-charcoal text-center mb-8">
            Add-Ons
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addOns.map((addon, index) => (
              <div key={index} className="flex items-center justify-between bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-coral to-pink rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-add-line text-white text-xl"></i>
                  </div>
                  <span className="text-sm font-medium text-charcoal">{addon.name}</span>
                </div>
                <span className="text-sm font-bold text-coral whitespace-nowrap">{addon.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center mt-6 italic">
            *Fresh Rose Bouquet colour subject to availability
          </p>
        </motion.div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl border-2 border-charcoal p-8 md:p-12"
        >
          <h3 className="font-serif font-bold text-2xl text-charcoal text-center mb-6">
            Important Notes
          </h3>
          <ul className="space-y-3 max-w-3xl mx-auto">
            <li className="flex items-start gap-3 text-sm text-gray-700">
              <i className="ri-time-line text-coral mt-0.5 flex-shrink-0 text-lg"></i>
              <span>A minimum of 2 weeks notice is required for all bookings</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-700">
              <i className="ri-gift-line text-coral mt-0.5 flex-shrink-0 text-lg"></i>
              <span>All decor items are single-use and may be kept or discarded by the client</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-700">
              <i className="ri-tools-line text-coral mt-0.5 flex-shrink-0 text-lg"></i>
              <span>Cleanup/tear down services are optional and available by request</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-700">
              <i className="ri-hotel-line text-coral mt-0.5 flex-shrink-0 text-lg"></i>
              <span>Hotel access fees are paid by the client</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-700">
              <i className="ri-secure-payment-line text-coral mt-0.5 flex-shrink-0 text-lg"></i>
              <span>A non-refundable deposit is required to secure booking</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-gray-700">
              <i className="ri-map-pin-line text-coral mt-0.5 flex-shrink-0 text-lg"></i>
              <span>Travel fees may apply outside of Toronto/GTA</span>
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <button
            onClick={() => scrollToSection('quiz')}
            className="text-charcoal font-medium hover:text-coral transition-colors cursor-pointer"
          >
            Not sure? Take our quiz <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </motion.div>
      </div>
    </section>
  );
}
